import { useMemo, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import type { SendToBringResponse } from '@ricettario/shared';
import { api, ApiError } from '../api/client';
import { useRecipe } from '../hooks/useRecipes';
import { scaleQuantity, formatQuantity } from '../lib/scale';

type LocationState = { servings?: number } | null;

export default function SendToBringPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const initialServings = (location.state as LocationState)?.servings;
  const { data: recipe, loading, error } = useRecipe(id);

  const [servings, setServings] = useState<number | null>(initialServings ?? null);
  const [selected, setSelected] = useState<Set<string> | null>(null);
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  const [result, setResult] = useState<SendToBringResponse | null>(null);

  const currentServings = servings ?? recipe?.baseServings ?? 2;
  const selectedSet = useMemo(() => {
    if (selected) return selected;
    if (!recipe) return new Set<string>();
    return new Set(recipe.ingredients.map((i) => i.id!).filter(Boolean));
  }, [selected, recipe]);

  function toggle(idValue: string) {
    setSelected(() => {
      const next = new Set(selectedSet);
      if (next.has(idValue)) next.delete(idValue);
      else next.add(idValue);
      return next;
    });
  }

  async function onSend() {
    if (!recipe || sending) return;
    setSending(true);
    setSendError(null);
    try {
      const res = await api.post<SendToBringResponse>(
        `/api/v1/recipes/${recipe.id}/send-to-bring`,
        { servings: currentServings, ingredientIds: Array.from(selectedSet) },
      );
      setResult(res);
    } catch (err) {
      setSendError(err instanceof ApiError ? err.message : 'errore invio');
    } finally {
      setSending(false);
    }
  }

  if (loading) {
    return (
      <main className="max-w-xl mx-auto px-5 py-6 space-y-3">
        <div className="skeleton h-5 w-20" />
        <div className="skeleton h-8 w-1/2" />
      </main>
    );
  }
  if (error || !recipe) {
    return (
      <main className="max-w-xl mx-auto px-5 py-20 text-center space-y-5">
        <p className="text-brand text-lg font-bold">{error ?? 'Ricetta non trovata.'}</p>
        <Link to="/" className="btn-soft">← torna</Link>
      </main>
    );
  }

  if (result) {
    return (
      <main className="max-w-xl mx-auto px-5 py-16 text-center space-y-6 animate-riseIn">
        <div className="mx-auto h-20 w-20 rounded-full bg-sageSoft flex items-center justify-center text-5xl animate-pop">
          ✅
        </div>
        <h1 className="text-3xl font-extrabold tracking-tightest">Spesa aggiunta!</h1>
        <p className="text-muted">
          {result.sent.length} vo{result.sent.length === 1 ? 'ce' : 'ci'} nella lista{' '}
          <strong className="text-ink">Caluzzelli</strong> dell'app Bring.
        </p>
        {result.failed.length > 0 && (
          <div className="card p-5 text-left">
            <p className="text-sm font-bold text-brand mb-2">{result.failed.length} non invi{result.failed.length === 1 ? 'ata' : 'ate'}</p>
            <ul className="space-y-1.5 text-sm text-muted">
              {result.failed.map((f) => (
                <li key={f.name}>
                  <strong className="text-ink">{f.name}</strong> — {f.reason}
                </li>
              ))}
            </ul>
          </div>
        )}
        <button className="btn-brand" onClick={() => navigate(`/recipe/${recipe.id}`)}>
          Torna alla ricetta
        </button>
      </main>
    );
  }

  return (
    <main className="max-w-xl mx-auto px-5 pt-5 pb-28">
      <nav className="mb-5">
        <Link to={`/recipe/${recipe.id}`} className="btn-ghost">← {recipe.name}</Link>
      </nav>

      <header className="mb-6 animate-riseIn">
        <h1 className="text-[28px] font-extrabold tracking-tightest leading-[1.05]">
          Aggiungi alla <span className="text-brand">spesa</span>
        </h1>
        <p className="text-sm text-muted mt-2 leading-relaxed">
          Le quantità sono scalate per <strong className="text-ink">{currentServings}</strong>{' '}
          persone.
        </p>
      </header>

      <div className="card p-4 flex items-center justify-between mb-4 animate-riseIn" style={{ animationDelay: '60ms' }}>
        <span className="text-sm font-semibold">Porzioni</span>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setServings(Math.max(1, currentServings - 1))}
            className="h-9 w-9 rounded-full bg-surface hover:bg-line disabled:opacity-30"
            disabled={currentServings <= 1}
            aria-label="diminuisci"
          >
            −
          </button>
          <span className="font-bold text-xl w-10 text-center tabular-nums">{currentServings}</span>
          <button
            onClick={() => setServings(Math.min(50, currentServings + 1))}
            className="h-9 w-9 rounded-full bg-surface hover:bg-line disabled:opacity-30"
            disabled={currentServings >= 50}
            aria-label="aumenta"
          >
            +
          </button>
        </div>
      </div>

      <ul className="card divide-y divide-line mb-4 animate-riseIn" style={{ animationDelay: '120ms' }}>
        {recipe.ingredients.map((ing) => {
          const qty = scaleQuantity(ing.quantity, currentServings, recipe.baseServings);
          const checked = selectedSet.has(ing.id!);
          return (
            <li key={ing.id}>
              <label
                htmlFor={`sel-${ing.id}`}
                className="flex items-center gap-4 px-5 py-3.5 cursor-pointer"
              >
                <span
                  className={`h-6 w-6 rounded-full border-2 transition flex items-center justify-center shrink-0 ${
                    checked ? 'bg-brand border-brand text-white' : 'border-line'
                  }`}
                  aria-hidden
                >
                  {checked && (
                    <svg viewBox="0 0 12 12" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M2 6l3 3 5-6" />
                    </svg>
                  )}
                </span>
                <input
                  id={`sel-${ing.id}`}
                  type="checkbox"
                  checked={checked}
                  onChange={() => toggle(ing.id!)}
                  className="sr-only"
                />
                <span className="flex-1 font-medium">{ing.name}</span>
                <span className="text-muted font-mono text-sm tabular-nums">
                  {formatQuantity(qty)}
                  {ing.unit ? ` ${ing.unit}` : ''}
                </span>
              </label>
            </li>
          );
        })}
      </ul>

      <div className="flex items-center gap-2 mb-6">
        <button
          className="btn-ghost text-sm"
          onClick={() => setSelected(new Set(recipe.ingredients.map((i) => i.id!)))}
        >
          tutti
        </button>
        <span className="text-muted">·</span>
        <button className="btn-ghost text-sm" onClick={() => setSelected(new Set())}>
          nessuno
        </button>
      </div>

      {sendError && <p className="text-brand font-medium mb-4">{sendError}</p>}

      <div className="bottom-bar">
        <div className="max-w-xl mx-auto px-5">
          <button
            className="btn-brand w-full py-4"
            onClick={onSend}
            disabled={sending || selectedSet.size === 0}
          >
            {sending
              ? 'invio…'
              : `Invia ${selectedSet.size} alla lista`}
          </button>
        </div>
      </div>
    </main>
  );
}
